import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { AlertCircle, Check, X } from 'lucide-react';
import { LoaderButton } from 'components/common/LoadingButton';

export interface CascadeItem {
  /** Number of records affected, e.g. 14 */
  count: number;
  /** SINGULAR label, e.g. "Note" or "Contact — Anjali Reddy" */
  label: string;
  /** Plural form. Defaults to `label + 's'` — set it for irregulars ("Activity" → "Activities"). */
  labelPlural?: string;
  /**
   * A whole sentence in place of "{count} {label}", for consequences a count
   * and a noun cannot state honestly — deleting a tag removes it from records
   * rather than deleting them. Rendered without a bullet.
   */
  text?: string;
}

const cascadeLabel = ({ count, label, labelPlural }: CascadeItem) =>
  count === 1 ? label : (labelPlural ?? `${label}s`);

/** Word the user types to arm the delete button, unless a call site overrides it. */
const DEFAULT_CONFIRM_WORD = 'Delete';

export interface DeleteModalBlockedState {
  /** Why deletion is not allowed right now. */
  reason: React.ReactNode;
  /** Bullet list of what the user must do first. */
  requirements?: string[];
}

export interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;

  /** Singular, title-cased entity type: "Contact", "Company", "Deal", "Note", "Task", "Department". */
  entityLabel: string;
  /** The record's own name: "Anjali Reddy". Omit for unnamed records (Note, Task). */
  entityName?: string;
  /** Overrides the default lead sentence. */
  description?: React.ReactNode;

  /** Red bullets — everything that gets destroyed. */
  cascade: CascadeItem[];
  /** Heading above the cascade block. Defaults to "This will permanently delete:". */
  cascadeTitle?: string;
  /** Green callout — what survives. Omit to hide the block entirely. */
  preserved?: React.ReactNode;

  /**
   * String the user must type to enable the delete button. Defaults to "Delete".
   * Pass the record's name for an unusually destructive delete you want to slow down.
   */
  confirmWord?: string;

  /** When present, the modal renders a read-only "cannot delete yet" state. */
  blocked?: DeleteModalBlockedState;
}

export const DeleteModal: React.FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
  entityLabel,
  entityName,
  description,
  cascade,
  cascadeTitle = 'This will permanently delete:',
  preserved,
  confirmWord,
  blocked,
}) => {
  const [confirmText, setConfirmText] = useState('');

  const requiredWord = useMemo(
    () => (confirmWord?.trim() || DEFAULT_CONFIRM_WORD),
    [confirmWord],
  );

  const isConfirmed = confirmText.trim().toLowerCase() === requiredWord.toLowerCase();
  const canDelete = isConfirmed && !isDeleting;

  useEffect(() => {
    if (!isOpen) setConfirmText('');
  }, [isOpen]);

  // Never let a backdrop click or Esc close the dialog mid-request.
  const handleClose = () => {
    if (!isDeleting) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canDelete) {
      e.preventDefault();
      onConfirm();
    }
  };

  const leadText =
    description ??
    (entityName ? (
      <>
        <strong className="text-white font-semibold">{entityName}</strong> will be permanently deleted.
      </>
    ) : (
      <>This {entityLabel.toLowerCase()} will be permanently deleted.</>
    ));

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[440px] transform overflow-hidden rounded-2xl bg-[#131720] border border-[#232736] p-6 text-left align-middle shadow-2xl transition-all relative">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-500/15 border border-red-500/30 text-red-500 flex items-center justify-center shrink-0">
                      <AlertCircle size={20} />
                    </div>
                    <DialogTitle as="h3" className="text-lg font-bold text-white tracking-wide">
                      Delete {entityLabel}
                    </DialogTitle>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    className="text-[#adaebc] hover:text-white transition-colors focus:outline-none disabled:opacity-40"
                    onClick={handleClose}
                    disabled={isDeleting}
                  >
                    <X size={18} />
                  </button>
                </div>

                {blocked ? (
                  <>
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl mb-4 text-xs text-red-300 leading-relaxed">
                      {blocked.reason}
                    </div>

                    {blocked.requirements && blocked.requirements.length > 0 && (
                      <div className="bg-[#181C28] border border-[#232736] rounded-xl p-4 mb-5">
                        <h4 className="text-xs font-semibold text-white tracking-wide mb-2">
                          Action required before deletion
                        </h4>
                        <ul className="space-y-1.5 text-xs text-[#adaebc]">
                          {blocked.requirements.map((req, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-[#adaebc] border border-[#232736] hover:bg-white/5 hover:text-white transition-all cursor-pointer"
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Lead sentence */}
                    <p className="text-xs text-[#adaebc] leading-relaxed mb-4">{leadText}</p>

                    {/* Cascade — what gets destroyed */}
                    {cascade.length > 0 && (
                      <div className="bg-[#181C28] border border-[#232736] rounded-xl p-4 mb-4">
                        <h4 className="text-xs font-semibold text-white tracking-wide mb-2">
                          {cascadeTitle}
                        </h4>
                        <ul className="space-y-1.5 text-xs text-[#adaebc]">
                          {cascade.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              {item.text ? (
                                <span className="text-white">{item.text}</span>
                              ) : (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0 mt-1.5" />
                                  <span>
                                    {item.count} {cascadeLabel(item)}
                                  </span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Preserved — what survives */}
                    {preserved && (
                      <div className="flex items-start gap-2.5 bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-3 mb-4">
                        <Check size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-emerald-200/90 leading-relaxed">{preserved}</p>
                      </div>
                    )}

                    <p className="text-xs font-semibold text-red-500 mb-3">This action cannot be undone.</p>

                    {/* Type-to-confirm */}
                    <div className="mb-5">
                      <label
                        htmlFor="delete-confirm-input"
                        className="block text-[10px] font-semibold text-[#adaebc] uppercase tracking-wider mb-2"
                      >
                        To confirm deletion, type{' '}
                        <span className="text-white font-bold normal-case">{requiredWord}</span>{' '}
                        below
                      </label>
                      <input
                        id="delete-confirm-input"
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isDeleting}
                        placeholder={`Type ${requiredWord} to confirm`}
                        className="w-full bg-[#181C28] border border-[#232736] focus:border-red-500/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 outline-none transition-colors disabled:opacity-50"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                      />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={isDeleting}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-[#adaebc] border border-[#232736] hover:bg-white/5 hover:text-white transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <LoaderButton
                        type="button"
                        onClick={onConfirm}
                        disabled={!canDelete}
                        isLoading={isDeleting}
                        text={`Delete ${entityLabel}`}
                        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all !mt-0 ${
                          canDelete
                            ? '!bg-red-600 hover:!bg-red-700 !text-white cursor-pointer shadow-lg shadow-red-500/20'
                            : '!bg-[#1e2230] !text-gray-500 cursor-not-allowed border border-[#2a2f3d]'
                        }`}
                      />
                    </div>
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteModal;